import { getEnv } from '@unni/platform'

import { HospitalService } from '@/features/hospital/api/hospital.service'
import { createSolarApiClient } from '@/shared/libs/api-client'
import { type AxiosRequestHeaders } from 'axios'
import { OptionalAgreeType } from '@/components/atomic/organisms/NormalConsultationBottomSheet'
import { HospitalModuleProps, SquareReviewElementProps } from '@/features/doctors/[doctorId]/index/helpers/types'
import { DoctorService } from '@/services/doctors/doctor.service'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import { getDefaultMeta } from '@/shared/utils'
import { CommunityDocumentService } from '@/features/community/api/document'
import { DoctorsDoctorId } from '@/features/doctors/[doctorId]/index/screen/doctors-doctor-id'
import { GlobalPageProps } from '@/pages/_app'
import { Locales } from '@/utils/language'
import { CommunityOfDoctor, DoctorInfo } from '@/features/doctors/[doctorId]/helpers/types/doctor'
import { convertResponseToDoctorInfo } from '@/features/doctors/[doctorId]/helpers/utils/doctor-info-converter'
import {
  convertReviewToSquareReviewElementProps,
  convertHospitalToHospitalModuleProps,
} from '@/features/doctors/[doctorId]/index/helpers/utils/doctor-detail-data-converters'

import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { getDoctorReviewsInternal } from '@/features/review/api/get-hospital-reviews'

interface DoctorDetailPageProps extends GlobalPageProps {
  userId: number
  userCountry: string | null
  userProfileImage: string | null
  doctorInfo: DoctorInfo
  reviews: SquareReviewElementProps[]
  hospitalModuleProps: HospitalModuleProps
  community: CommunityOfDoctor | null
  optionalMarketingItems: (keyof OptionalAgreeType)[]
}

const DoctorDetailPage = ({
  userId,
  userCountry,
  userProfileImage,
  doctorInfo,
  reviews,
  hospitalModuleProps,
  community,
  optionalMarketingItems,
}: DoctorDetailPageProps) => {
  return (
    <DoctorsDoctorId
      userId={userId}
      userCountry={userCountry}
      userProfileImage={userProfileImage}
      doctorInfo={doctorInfo}
      reviews={reviews}
      hospitalModuleProps={hospitalModuleProps}
      community={community}
      optionalMarketingItems={optionalMarketingItems}
    />
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<DoctorDetailPageProps>> => {
  const contextEnhancer = new ContextEnhancer(ctx)
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
  try {
    const numberedDoctorId = Number(enhancedContext.query.doctorId)
    const language = enhancedContext.locale

    const optionalMarketingItems: (keyof OptionalAgreeType)[] = []
    !enhancedContext.user.sendPushAd && optionalMarketingItems.push('sendPushAd')
    !enhancedContext.user.agreeEmail && optionalMarketingItems.push('agreeEmail')
    !enhancedContext.user.agreeSms && optionalMarketingItems.push('agreeSms')

    if (isNaN(numberedDoctorId)) {
      throw new Error('Invalid doctorId')
    }

    const doctorService = new DoctorService(
      createSolarApiClient({ headers: enhancedContext.apiHeaders as unknown as AxiosRequestHeaders }),
    )
    const [doctorResult, reviewsResult] = await Promise.allSettled([
      doctorService.getDoctor(numberedDoctorId),
      getDoctorReviewsInternal(
        {
          Authorization: enhancedContext.token,
          'Accept-Language': language,
        },
        numberedDoctorId,
        {
          pageIndex: 0,
          pageSize: 6,
        },
      ),
    ])

    if (doctorResult.status !== 'fulfilled') {
      throw new Error('의사 정보를 불러오지 못했습니다.')
    }
    if (reviewsResult.status !== 'fulfilled') {
      throw new Error('리뷰 정보를 불러오지 못했습니다.')
    }

    const doctorResponse = doctorResult.value
    const reviewsResponse = reviewsResult.value

    const hospitalService = new HospitalService(
      createSolarApiClient({ headers: enhancedContext.apiHeaders as unknown as AxiosRequestHeaders }),
    )
    const hospitalResponse = await hospitalService.getHospital(doctorResponse.hospitalId)

    const community: CommunityOfDoctor = {
      documents: {
        content: [],
        totalElements: 0,
      },
      comments: {
        content: [],
        totalElements: 0,
      },
    }

    if (doctorResponse.doctorId) {
      const params = {
        memberId: doctorResponse.doctorId,
        pageIndex: 0,
        pageSize: 3,
      }
      const communityDocumentService = new CommunityDocumentService(
        createSolarApiClient({ headers: enhancedContext.apiHeaders as unknown as AxiosRequestHeaders }),
      )
      const [documentsResult, commentsResult] = await Promise.allSettled([
        communityDocumentService.getDocumentsByDoctor(params),
        communityDocumentService.getCommentsByDoctor(params),
      ])

      if (documentsResult.status === 'fulfilled' && documentsResult.value !== null) {
        community.documents = documentsResult.value
      } else if (documentsResult.status === 'rejected') {
        console.error('[getServerSideProps] getDocumentsByDoctor 실패:', documentsResult.reason)
      }

      if (commentsResult.status === 'fulfilled' && commentsResult.value !== null) {
        community.comments = commentsResult.value
      } else if (commentsResult.status === 'rejected') {
        console.error('[getServerSideProps] getCommentsByDoctor 실패:', commentsResult.reason)
      }
    }

    const meta = getDefaultMeta(enhancedContext.locale, (meta) => ({
      title:
        enhancedContext.locale === Locales.TH_TH
          ? `${doctorResponse.hospitalName} ${doctorResponse.name} | UNNI - แอปเกาหลีอันดับ 1`
          : `${doctorResponse.hospitalName} ${doctorResponse.name} | ${meta.title}`,
      canonical: `${getEnv('NEXT_PUBLIC_HOST')}${enhancedContext.pathnamePrefix}/doctors/${numberedDoctorId}`,
    }))

    return {
      props: {
        userId: enhancedContext.user.id,
        userCountry: enhancedContext.user.country,
        userProfileImage: enhancedContext.user.profileImage,
        community,
        doctorInfo: convertResponseToDoctorInfo(doctorResponse),
        reviews: convertReviewToSquareReviewElementProps(reviewsResponse.contents),
        hospitalModuleProps: convertHospitalToHospitalModuleProps(hospitalResponse),
        optionalMarketingItems,
        user: enhancedContext.user,
        meta,
      },
    }
  } catch (error) {
    console.error('[getServerSideProps] 에러 발생:', error)
    if (enhancedContext && enhancedContext.pathnamePrefix) {
      return {
        redirect: {
          destination: `/${enhancedContext.pathnamePrefix}/hospitals`,
          permanent: false,
        },
      }
    }
    // enhancedContext가 없을 때 fallback
    return {
      redirect: {
        destination: `/hospitals`,
        permanent: false,
      },
    }
  }
}

export default DoctorDetailPage
